import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { DatePickerComponent, ChangedEventArgs } from '@syncfusion/ej2-react-calendars';
import { SwitchComponent } from '@syncfusion/ej2-react-buttons';
import moment from 'moment';
import { ActionBarComponent } from '@aitex/tsx-extranet-action-bar';
import { FormValidator, FormEventArgs } from '@syncfusion/ej2-inputs';
import * as formValidatorUtils from '../../../utils/formValidator.utils';
import FormError from '../../../components/formError/formError';
import { IErrors } from '../../../common/model/errors.model';
import { Validator } from '../../../common/model/enumerations/validator.model';
import ValidationError from '../../../components/validationError/validationError';
import { IInfoCardProps } from '../../../interfaces/props/IWebinarDetailProps';
import TextBox from '../../../components/filters/textBox';
import './infoCard.scss';

type InfoCardPropsType = IInfoCardProps & WithTranslation;

class ClientDataTab extends Component<InfoCardPropsType> {
  public readonly WEBINAR_DETAIL_FORM_ID = 'webinarDetailForm';

  public dateDatePickerComponent: DatePickerComponent = null;

  private formValidator: FormValidator = null;
  private isValidatingAll = false;
  private auxErrors: IErrors = {};

  public constructor(props: InfoCardPropsType) {
    super(props);
  }

  public configureFormValidator(): void {
    if (!this.props.isView) {
      if (!this.formValidator) {
        this.formValidator = formValidatorUtils.configureFormValidator(this.WEBINAR_DETAIL_FORM_ID, {
          Name: { required: [true, Validator.NOT_EMPTY] },
          Speakers: { required: [true, Validator.NOT_EMPTY] },
          Date: { required: [true, Validator.NOT_EMPTY] },
          VideoUrl: { required: [true, Validator.NOT_EMPTY] }
        });

        this.formValidator.validationComplete = (args): void => {
          formValidatorUtils.validationComplete(args as FormEventArgs, this.isValidatingAll, this.props.errors, this.auxErrors, (errors) => this.props.setErrors(errors));
        };
      }
    } else {
      this.formValidator = null;
    }
  }

  public render(): JSX.Element {
    const {
      webinar,
      isView,
      isNew,
      isSaving,
      onEditButtonClick,
      onSaveButtonClick,
      onCancelButtonClick,
      onNameChange,
      onSpeakersChange,
      onDateChange,
      onDescriptionChange,
      onVideoUrlChange,
      onActiveChange,
      i18n,
      errors
    } = this.props;

    const isoWeekday = moment().isoWeekday();
    const firstDayOfWeek = isoWeekday - 1;

    return (
      <div className='mb-3'>
        <div className='webinar-detail-info-card'>
          <form id={this.WEBINAR_DETAIL_FORM_ID} ref={(): void => this.configureFormValidator()}>
            <FormError errors={errors}/>
            <div className='row'>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='autocomplete-input'>
                    <div className='autocomplete-label'>{i18n.t('webinar.name')}</div>
                    <TextBox
                      id='name'
                      name='Name'
                      value={webinar.name}
                      readonly={isView}
                      onChange={(e: string) => {
                        onNameChange(e);

                        if (this.formValidator) {
                          this.formValidator.validate('Name');
                        }
                      }}/>
                  </div>
                  <ValidationError errors={errors} errorKey={'Name'}/>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='autocomplete-input'>
                    <div className='autocomplete-label'>{i18n.t('webinar.speakers')}</div>
                    <TextBox
                      id='speakers'
                      name='Speakers'
                      value={webinar.speakers}
                      readonly={isView}
                      onChange={(e: string) => {
                        onSpeakersChange(e);

                        if (this.formValidator) {
                          this.formValidator.validate('Speakers');
                        }
                      }} />
                  </div>
                  <ValidationError errors={errors} errorKey={'Speakers'} />
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='col-12 col-md-6'>
                <div className='form-group'>
                  <div className='autocomplete-input'>
                    <div className='autocomplete-label'>{i18n.t('webinar.date')}</div>
                    <DatePickerComponent
                      id='date'
                      name='Date'
                      readonly={isView}
                      locale={i18n.language}
                      ref={(datePicker: DatePickerComponent): DatePickerComponent => this.dateDatePickerComponent = datePicker}
                      created={() => this.dateDatePickerComponent.value = webinar.date && webinar.date.toDate()}
                      change={(e: ChangedEventArgs) => onDateChange(e.value && moment(e.value).isValid() ? moment(e.value) : null)}
                      firstDayOfWeek={firstDayOfWeek}
                    />
                  </div>
                </div>
                <ValidationError errors={errors} errorKey={'date'} />
              </div>
              <div className='col-12 col-md-6'>
                <div className='form-group d-flex' style={{ marginTop: '7px' }}>
                  <label htmlFor='active' className='mr-2'>{i18n.t('webinar.active')}</label>
                  <div style={{ marginTop: '0.15rem' }}>
                    <SwitchComponent
                      id='active'
                      checked={webinar.active}
                      disabled={isView}
                      change={(e: any) => onActiveChange(e.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='col-12 col-md-12'>
                <div className='form-group'>
                  <div className='autocomplete-input'>
                    <div className='autocomplete-label'>{i18n.t('webinar.videoUrl')}</div>
                    <TextBox
                      id='videoUrl'
                      name='VideoUrl'
                      value={webinar.videoUrl}
                      readonly={isView}
                      onChange={(e: string) => {
                        onVideoUrlChange(e);

                        if (this.formValidator) {
                          this.formValidator.validate('VideoUrl');
                        }
                      }} />
                  </div>
                  <ValidationError errors={errors} errorKey={'VideoUrl'} />
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='col'>
                <div className='form-group'>
                  <label htmlFor='description'>{i18n.t('webinar.description')}</label>
                  <textarea
                    className='form-control'
                    id='description'
                    rows={5}
                    value={webinar.description}
                    readOnly={isView}
                    required={true}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onDescriptionChange(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <ActionBarComponent
          disabled={false}
          editing={isView === false}
          loading={isSaving}
          withoutTopBorder={true}
          onEditButtonClick={isNew ? undefined : onEditButtonClick}
          onSaveButtonClick={() => {
            if (this.validateForm()) {
              onSaveButtonClick();
              this.formValidator = null;
            }
          }}
          onCancelButtonClick={() => {
            this.props.setErrors({});
            formValidatorUtils.clearErrors(this.formValidator);
            this.formValidator = null;
            onCancelButtonClick();
          }}
        />
      </div>
    );
  }

  private validateForm(): boolean {
    if (!this.formValidator) {
      return true;
    }

    this.isValidatingAll = true;
    const isFormValid = this.formValidator.validate();
    this.isValidatingAll = false;
    if (!isFormValid) {
      this.props.setErrors(this.auxErrors);
      this.auxErrors = {};
    }
    return isFormValid;
  }
}

export default withTranslation()(ClientDataTab);
